import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useMerchant, useUpdateCouponStatus } from '@/hooks/useVendors';
import { useNotifications } from '@/hooks/useNotifications';
import { CouponFormDialog } from './CouponFormDialog';

function truncateWords(text: string, maxWords = 5) {
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length <= maxWords) return text;
  return `${words.slice(0, maxWords).join(' ')}...`;
}

export function VendorDetailPage() {
  const { merchantId } = useParams<{ merchantId: string }>();
  const navigate = useNavigate();
  const id = Number(merchantId);
  const { data: merchant, isLoading } = useMerchant(id);
  const [isCouponFormOpen, setIsCouponFormOpen] = useState(false);

  const { success, error: showToastError } = useNotifications();
  const { mutate: updateStatus, isPending: isUpdatingStatus } = useUpdateCouponStatus();

  const coupons = merchant?.coupons ?? [];

  const handleToggleStatus = (couponId: number, status: 'Active' | 'Inactive') => {
    updateStatus(
      { couponId, status, merchantId: id },
      {
        onSuccess: () => {
          success(`Coupon status updated to ${status}.`);
        },
        onError: (err) => {
          showToastError(err.response?.data?.message || 'Failed to update status.');
        },
      }
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/vendors')}
          className="mb-2 -ml-2"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Merchant
        </Button>

        {isLoading && <p className="text-muted-foreground">Loading vendor...</p>}

        {!isLoading && merchant && (
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-3xl font-bold tracking-tight">{merchant.name}</h2>
              <p className="max-w-2xl text-muted-foreground" title={merchant.description}>
                {merchant.description}
              </p>
            </div>
            <Button onClick={() => setIsCouponFormOpen(true)}>Add Coupon</Button>
          </div>
        )}
      </div>

      <div className="rounded-lg border">
        <div className="overflow-x-auto">
          <table className="w-full whitespace-nowrap text-sm">
            <thead>
              <tr className="border-b bg-muted/50 text-left text-muted-foreground">
                <th className="p-3 font-medium">Image</th>
                <th className="p-3 font-medium">Title</th>
                <th className="p-3 font-medium min-w-[250px]">Description</th>
                <th className="p-3 font-medium">Discount</th>
                <th className="p-3 font-medium">Coupon Value</th>
                <th className="p-3 font-medium">Total</th>
                <th className="p-3 font-medium">Redeemed</th>
                <th className="p-3 font-medium">Remaining</th>
                <th className="p-3 font-medium">Valid Till</th>
                <th className="p-3 font-medium">Status</th>
                <th className="p-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={11} className="p-6 text-center text-muted-foreground">
                    Loading coupons...
                  </td>
                </tr>
              )}
              {!isLoading && coupons.length === 0 && (
                <tr>
                  <td colSpan={11} className="p-6 text-center text-muted-foreground">
                    No coupons found.
                  </td>
                </tr>
              )}
              {coupons.map((coupon) => (
                <tr key={coupon.id} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="p-3">
                    {coupon.image ? (
                      <img src={coupon.image} alt="" className="h-9 w-9 rounded-md object-cover" />
                    ) : (
                      <div className="h-9 w-9 rounded-md bg-muted" />
                    )}
                  </td>
                  <td className="p-3 font-medium">{coupon.title}</td>
                  <td className="max-w-xs whitespace-normal p-3" title={coupon.description}>
                    {truncateWords(coupon.description)}
                  </td>
                  <td className="p-3">{coupon.discountLabel}</td>
                  <td className="p-3">{coupon.pointsCost}</td>
                  <td className="p-3">{coupon.totalCount}</td>
                  <td className="p-3">{coupon.redeemedCount}</td>
                  <td className="p-3">{coupon.remainingCount}</td>
                  <td className="p-3">{new Date(coupon.validTill).toLocaleDateString()}</td>
                  <td className="p-3">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${coupon.status?.toLowerCase() === 'active'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                        }`}
                    >
                      {coupon.status}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <Switch
                      checked={coupon.status?.toLowerCase() === 'active'}
                      onCheckedChange={(checked) => handleToggleStatus(coupon.id, checked ? 'Active' : 'Inactive')}
                      disabled={isUpdatingStatus}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {merchant && (
        <CouponFormDialog
          open={isCouponFormOpen}
          onOpenChange={setIsCouponFormOpen}
          merchantId={merchant.id}
        />
      )}
    </div>
  );
}
